<script setup>
import { reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'next/button/Button.vue';

const props = defineProps({
  modelValue: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();
const { currentAccount, updateAccount } = useAccount();

const systemStages = [
  'draft',
  'inquiry',
  'confirmed',
  'completed',
  'lost',
  'archived',
];

const STAGE_LABELS = {
  draft: 'PLANNING.EVENTS.STAGES.DRAFT',
  inquiry: 'PLANNING.EVENTS.STAGES.INQUIRY',
  confirmed: 'PLANNING.EVENTS.STAGES.CONFIRMED',
  completed: 'PLANNING.EVENTS.STAGES.COMPLETED',
  lost: 'PLANNING.EVENTS.STAGES.LOST',
  archived: 'PLANNING.EVENTS.STAGES.ARCHIVED',
};

const formatStageLabel = stage => t(STAGE_LABELS[stage]);

const form = reactive({
  without_allocation: 'draft',
  with_allocation: 'inquiry',
});

const isSaving = ref(false);

const loadDefaults = () => {
  const stored =
    currentAccount.value?.settings?.planning_event_stage_defaults || {};
  form.without_allocation = stored.without_allocation || 'draft';
  form.with_allocation = stored.with_allocation || 'inquiry';
};

const close = () => {
  emit('update:modelValue', false);
};

const save = async () => {
  isSaving.value = true;
  try {
    await updateAccount(
      {
        planning_event_stage_defaults: {
          without_allocation: form.without_allocation,
          with_allocation: form.with_allocation,
        },
      },
      { silent: true }
    );
    useAlert(t('PLANNING.STATUSES.DEFAULTS.SAVE_SUCCESS'));
    close();
  } catch {
    useAlert(t('PLANNING.STATUSES.DEFAULTS.SAVE_ERROR'));
    loadDefaults();
  } finally {
    isSaving.value = false;
  }
};

watch(
  () => props.modelValue,
  open => {
    if (open) loadDefaults();
  }
);
</script>

<template>
  <div
    v-if="modelValue"
    class="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm"
    @click.self="close"
  >
    <div
      class="w-full max-w-lg rounded-xl bg-white dark:bg-slate-800 shadow-2xl mx-4 overflow-hidden"
    >
      <div class="px-6 py-5 border-b border-n-weak">
        <h3 class="text-base font-semibold text-slate-900 dark:text-white">
          {{ t('PLANNING.STATUSES.DEFAULTS.TITLE') }}
        </h3>
        <p class="mt-1 text-sm text-slate-600 dark:text-slate-300">
          {{ t('PLANNING.STATUSES.DEFAULTS.DESCRIPTION') }}
        </p>
      </div>

      <div class="px-6 py-5 space-y-4">
        <div>
          <label class="block text-sm font-medium text-n-slate-11 mb-1">
            {{ t('PLANNING.STATUSES.DEFAULTS.WITHOUT_ALLOCATION') }}
          </label>
          <select
            v-model="form.without_allocation"
            class="reset-base w-full border border-n-weak rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-800"
          >
            <option v-for="stage in systemStages" :key="stage" :value="stage">
              {{ formatStageLabel(stage) }}
            </option>
          </select>
          <p class="mt-1 text-xs text-n-slate-11">
            {{ t('PLANNING.STATUSES.DEFAULTS.WITHOUT_ALLOCATION_HINT') }}
          </p>
        </div>
        <div>
          <label class="block text-sm font-medium text-n-slate-11 mb-1">
            {{ t('PLANNING.STATUSES.DEFAULTS.WITH_ALLOCATION') }}
          </label>
          <select
            v-model="form.with_allocation"
            class="reset-base w-full border border-n-weak rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-800"
          >
            <option v-for="stage in systemStages" :key="stage" :value="stage">
              {{ formatStageLabel(stage) }}
            </option>
          </select>
          <p class="mt-1 text-xs text-n-slate-11">
            {{ t('PLANNING.STATUSES.DEFAULTS.WITH_ALLOCATION_HINT') }}
          </p>
        </div>
      </div>

      <div class="px-6 py-4 border-t border-n-weak flex items-center gap-2">
        <Button
          :label="t('PLANNING.STATUSES.FORM.CANCEL')"
          slate
          outlined
          class="w-full"
          @click="close"
        />
        <Button
          :label="t('PLANNING.STATUSES.DEFAULTS.SAVE')"
          class="w-full"
          :is-loading="isSaving"
          @click="save"
        />
      </div>
    </div>
  </div>
</template>
